import React from "react";
import CalculationSection from "./calculation-section";
import { useAppSelector } from "../../store/hooks/StoreHooks";
import { RoleConstants } from "../../utilities/AppConstants";

const CalculationHelper: React.FC = () => {
  const { customerEnabled, user } = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
  const location = window?.location?.href;
  const [activeSection, setActiveSection] = React.useState<string>("introduction");

  React.useEffect(() => {
    const sectionIds = [
      "introduction",
      "user-manual",
      ...( !customerEnabled && !projectReadOnly && !user?.userRole?.includes(RoleConstants.user) ? ["calculations"] : [] ),
      "appendix"
    ];

    const handleScroll = () => {
      let closestSection = sectionIds[0];
      let minDistance = Number.POSITIVE_INFINITY;

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          const distance = Math.abs(rect.top - 80);
          if (distance < minDistance) {
            minDistance = distance;
            closestSection = id;
          }
        }
      }
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 2
      ) {
        closestSection = sectionIds[sectionIds.length - 1];
      }
      setActiveSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [customerEnabled, projectReadOnly, user]);

  const getNavClass = (section: string) =>
    `cursor-pointer text-[16px] font-bold hover:text-primary-400 ${
      activeSection === section ? "text-primary-400" : ""
    }`;
  return (
    <>
      <nav className="px-[32px] pt-[24px] pb-[20px] w-full bg-darkcolor-700 sticky top-0 z-[1001]">
        <div className="flex justify-between text-white">
          <div>
            <p className="text-[28px] font-semibold text-neutral-25">
              DETERMINE
              <sup>
                <small>
                  <small>
                    <b>TM</b>
                  </small>
                </small>
              </sup>{" "}
              ESP sizing guide
            </p>
          </div>
          <div className="flex items-stretch border-[1px] rounded-[8px] border-darkcolor-400 overflow-hidden">
            <div className="nav-item text-neutral-25 flex items-center px-[16px]">
              <a
                href="#introduction"
                className={getNavClass("introduction")}
              >
                Introduction
              </a>
            </div>
            <div className="w-px bg-darkcolor-400" />
            <div className="nav-item text-neutral-25 flex items-center px-[16px]">
              <a
                href="#user-manual"
                className={getNavClass("user-manual")}
              >
                User manual
              </a>
            </div>
            {!customerEnabled &&
              !projectReadOnly &&
              !user?.userRole?.includes(RoleConstants.user) && (
                <>
                  <div className="w-px bg-darkcolor-400" />
                  <div className="nav-item text-neutral-25 flex items-center px-[16px]">
                    <a
                      href="#calculations"
                      className={getNavClass("calculations")}
                    >
                      Calculations
                    </a>
                  </div>
                </>
              )}
            <div className="w-px bg-darkcolor-400" />
            <div className="nav-item text-neutral-25 flex items-center pl-[16px]">
              <a
                href="#appendix"
                className={getNavClass("appendix")}
              >
                Appendix
              </a>
            </div>
            <div className="nav-item text-primary-400 flex items-center pl-[16px]">
              {/* <a href='#version-history'>Version History</a> */}
            </div>
          </div>
        </div>
      </nav>

      <div className="px-[32px] py-[24px]">
        <div
          id="wrapper"
          className="d-flex flex-column"
          style={{ color: "#fff" }}
        >
          <div className="d-flex flex-col p-2">
            <section id="top" className="flex flex-col gap-[16px] pt-0">
              <div className="col-md-12 left-align bg-darkcolor-700 border-[1px] rounded-[8px] border-darkcolor-400">
                <div className="px-[24px] py-[16px] border-b-[1px] border-darkcolor-400">
                  <p
                    id="introduction"
                    className="text-neutral-25 text-[18px] font-semibold"
                  >
                    Introduction
                  </p>
                </div>
                <div className="d-flex p-[24px]">
                  <div className="col-md-12 full">
                    <p className="text-white font-normal text-[16px]">
                      The electrical submersible pump, typically called an ESP,
                      is an efficient and reliable artificial-lift method for
                      lifting moderate to high volumes of fluids from wellbores.
                      These volumes range from a low of 150 B/D to as much as
                      150,000 B/D (24 to 24,600 m3/d). Variable-speed
                      controllers can extend this range significantly, both on
                      the high and low side. The ESP’s main components include:
                      <div className="py-[16px]">
                        <ul className="flex flex-col list-disc pl-6">
                          <li>
                            Gas handling devices (Gas separators / GKX / Charge
                            pumps)
                          </li>
                          <li>Multistaged centrifugal pump</li>
                          <li>Three-phase induction motor</li>
                          <li>Seal-chamber section</li>
                          <li>Power cable</li>
                          <li>Surface controls</li>
                        </ul>
                      </div>
                      <div className="text-white font-semibold text-[16px]">
                        The DETERMINE
                        <sup>
                          <small>
                            <small>
                              <b>TM</b>
                            </small>
                          </small>
                        </sup>{" "}
                        ESP sizing software helps automate this process of
                        sizing and equipment selection.
                      </div>
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-12 left-align bg-darkcolor-700 border-[1px] rounded-[8px] border-darkcolor-400">
                <div className="px-[24px] py-[16px] border-b-[1px] border-darkcolor-400">
                  <p
                    id="user-manual"
                    className="text-neutral-25 text-[18px] font-semibold scroll-mt-10"
                  >
                    User manual
                  </p>
                </div>
                <div className="d-flex p-[24px]">
                  <div className="col-md-12 full">
                    <div className="col-md-12">
                      <h4
                        id="line6_1"
                        className="text-[18px] text-white font-semibold"
                      >
                        Login
                      </h4>
                      <p className="pt-4">
                        A user can log in with their email id/username, and
                        their password. The functionality is explained below:
                      </p>
                      <div className="py-[16px]">
                        <ul className="flex flex-col list-disc pl-6">
                          <li key="1">
                            Email id or username of the user needs to be entered
                            here. A first time user needs to enter with their
                            email address here, and can add a username in the
                            profile page which can be used for login thereafter.
                          </li>
                          <li key="2">
                            The password needs to be entered here. A first time
                            user would receive their password from the Admin and
                            the password can be changed from the profile page of
                            the user after log in.
                          </li>
                          <li key="3">
                            Once the correct username/email and password is
                            entered, the user can click the ‘Log in’ button to
                            get access to the dashboard of the software.
                          </li>
                          <li key="4">
                            The ‘Reset’ button empties the username/email and
                            password fields for the user to enter a different
                            username/email and password.
                          </li>
                          <li key="5">
                            The user may click on ‘Forgot password?’ to contact
                            the admin in case the password of the user is
                            misplaced.
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 pt-[16px]">
                      <h4
                        id="line6_1"
                        className="text-[18px] text-white font-semibold"
                      >
                        Create/Edit template
                      </h4>
                      <p className="pt-4">
                        After log in, the application dashboard is displayed, as
                        shown above. On clicking the arrow next to the username
                        on the top right corner, a list of options appear. On
                        clicking the ‘Templates’ option, the page to create/edit
                        a template opens, as shown below.
                      </p>
                      <p className="pt-2">
                        The template screen shown above is explained further:
                      </p>
                      <div className="py-[16px]">
                        <ol className="felx flex-col list-disc pl-6">
                          <li>
                            Create Template: to create a new template. This is
                            further explained later.
                          </li>
                          <li>
                            Edit icon : to edit an existing template. This opens
                            a window, similar to the A. Create template window.
                            Any details the user wishes to alter can be changed
                            in this window and saved for future use.
                          </li>
                          <li>
                            Delete icon : to delete an existing template. On
                            clicking this icon, a dialog box opens asking for
                            confirmation, agreeing to which, the template will
                            be permanently deleted
                          </li>
                          <li>
                            Copy icon : to make a duplicate of an existing
                            template. On clicking this icon, a dialog box opens
                            asking for the name of the new template, after
                            entering which the user may click the ‘Copy’ button
                            to create a duplicate of the template under the
                            given name.
                            <ol className="pl-6 list-disc">
                              <li>
                                A unique template name can be entered, to easily
                                locate the created template for use
                              </li>
                              <li>
                                A basic unit system can be selected from the
                                dropdown list. This sets the default unit system
                                for the project so the user need not change each
                                unit individually.
                              </li>
                              <li>
                                This option allows the user to select the type
                                of template that is being created. Admin and
                                power users may create a global template (to be
                                accessible by all users) or a user type template
                                (for his personal use only). Other users,
                                however, can only create user type templates.
                              </li>
                              <li>
                                The rest of the template can be filled out by
                                the user based on requirements of the user. The
                                value and unit selected by the user in the
                                template is directly used in the inputs for each
                                project that is created using the template. The
                                dropdown boxes contain the list of units that
                                the user can choose from and this list changes
                                based on the unit system select by the user.
                              </li>
                              <li>
                                On completion the user may click ‘Save’ button
                                to save the template created.
                              </li>
                              <li>
                                The ‘Close’ button can be clicked when the user
                                wishes to close the dialog box without saving
                                any changes made.
                              </li>
                            </ol>
                          </li>
                        </ol>
                      </div>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 pt-[16px]">
                      <h4
                        id="line6_1"
                        className="text-[18px] text-white font-semibold"
                      >
                        Create/Edit project
                      </h4>
                      <p className="pt-4">
                        The top of the dashboard displays the templates
                        available to the user, as shown above. The global
                        templates (i.e., templates available to all users) are
                        displayed on the left whereas the user defined templates
                        (i.e., templated created and available only to a
                        particular user) is displayed on the right. A user may
                        click on any of the templates, to start a new project
                        with predefined values and unit systems as saved in the
                        template.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {!customerEnabled &&
                !user?.userRole?.includes(RoleConstants.user) && (
                  <div className="col-md-12 left-align bg-darkcolor-700 border-[1px] rounded-[8px] border-darkcolor-400">
                    <div className="d-flex">
                      <div className="col-md-12 full">
                        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 padding-none-important">
                          <div className="panel-body full-width panel-background">
                            <div className="panel-control flip pull-left width-full-important">
                              <div className="px-[24px] py-[16px] border-b-[1px] border-darkcolor-400">
                                <p
                                id="calculations"
                                className="text-neutral-25 text-[18px] font-semibold scroll-mt-10"
                              >
                                Calculations
                              </p>
                              </div>
                              <CalculationSection />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              <div className="col-md-12 left-align bg-darkcolor-700 border-[1px] rounded-[8px] border-darkcolor-400">
                <div className="px-[24px] py-[16px] border-b-[1px] border-darkcolor-400">
                  <p
                    id="appendix"
                    className="text-neutral-25 text-[18px] font-semibold scroll-mt-10"
                  >
                    Appendix
                  </p>
                </div>
                <div className="d-flex p-[24px]">
                  <div className="col-md-12 full">
                    <div className="col-md-12 pb-4 text-[16px] text-neutral-25">
                      <p
                        id="specific-gravity-of-water"
                        className="text-[16px] font-semibold pb-2"
                      >
                        Specific gravity of water
                      </p>
                      <p className="font-normal">
                        In ESP sizing applications, a value of 1.06 can be
                        assumed as the specific gravity of water. The specific
                        gravity is altered based on temperature and impurities
                        present. Assuming this value may not give accurately
                        calculated results.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="oil-api"
                        className="font-semibold pb-2"
                      >
                        Oil API
                      </p>
                      <p className="font-normal">
                        In ESP sizing applications, a value of 33 can be assumed
                        as the Oil API. It must be kept in mind that this is a
                        property of the fluid and assuming this value may not
                        give accurately calculated results.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="specific-gravity-of-oil"
                        className="font-semibold pb-2"
                      >
                        Specific gravity of oil
                      </p>
                      <p className="font-normal">
                        In ESP sizing applications, a value of 0.86 can be
                        assumed as the specific gravity of oil. It must be kept
                        in mind that the specific gravity of oil is highly
                        dependent on the type of oil, its composition and
                        temperature. Assuming this value may not give accurately
                        calculated results.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="fluid-viscosity"
                        className="font-semibold pb-2"
                      >
                        Fluid viscosity
                      </p>
                      <p className="font-normal">
                        In this particular application, a value of 1 cP (0.01 P,
                        0.1Pl or 0.1Pas) can be assumed as the dynamic viscosity
                        of the fluid. This is a property of the fluid, and
                        assuming this value may give inaccurate results.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="specific-gravity-of-gas"
                        className="font-semibold pb-2"
                      >
                        Specific gravity of gas
                      </p>
                      <p className="font-normal">
                        In ESP sizing applications, a value of 0.7 can be
                        assumed as the specific gravity of gas. It must be kept
                        in mind that the specific gravity of gas is highly
                        dependent on the type of gas, its composition and
                        temperature. Assuming this value may not give accurately
                        calculated results.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="gas-compressibility"
                        className="font-semibold pb-2"
                      >
                        Gas compressibility
                      </p>
                      <p className="pb-2 font-normal">
                        The compressibility factor (Z), also known as the
                        compression factor or the gas deviation factor, is a
                        correction factor which describes the deviation of a
                        real gas from ideal gas behavior. It is simply defined
                        as the ratio of the molar volume of a gas to the molar
                        volume of an ideal gas at the same temperature and
                        pressure.
                      </p>
                      <p className="font-normal">
                        In ESP calculations, the user may provide a measured
                        value of gas compressibility, or let the program use a
                        calculated value. By checking the 'Auto gas
                        compressibility', the factor would be auto calculated by
                        the program. Since gas compressibility is a compulsory
                        field, the user must provide a value or check the 'Auto
                        gas compressibility' check box to proceed with further
                        calculations.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="bubble-point-pressure"
                        className="font-semibold pb-2"
                      >
                        Bubble point pressure
                      </p>
                      <p className="pb-2 font-normal">
                        The pressure at which this natural gas begins to come
                        out of solution and form bubbles is known as the
                        bubble point pressure. This program uses the Standing's
                        correlation of 1947 as shown below.
                      </p>
                      <p className="font-normal">
                        The user may choose to enter the bubble point pressure
                        manually, or allow the program to automatically
                        calculate the maximum bubble point by checking the 'Max
                        bubble point' checkbox below the field. Since this is a
                        compulsory field, the user must provide a value or check
                        the 'Max bubble point' check box to proceed with further
                        calculations.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 py-4 text-[16px] text-neutral-25">
                      <p
                        id="surface-temperature"
                        className="font-semibold pb-2"
                      >
                        Surface temperature
                      </p>
                      <p className="font-normal">
                        The surface temperature is highly dependent on the
                        region of use. For calculations, an average temperature
                        of 20°C (68°F) can be assumed.
                      </p>
                    </div>
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="col-md-12 pt-4 text-[16px] text-neutral-25">
                      <p
                        id="operating-frequency"
                        className="font-semibold pb-2"
                      >
                        Operating frequency
                      </p>
                      <p className="font-normal">
                        The standard operating frequency differs from region to
                        region. Standard operating frequency in the United
                        States of America is 60Hz, whereas that in UK is 50Hz.
                        This is a vital parameter that affects equipment
                        selection and an accurate value must be entered for
                        proper sizing and functionality.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* <section className='section'>
              <div className='col-md-12 left-align mt-10'>
                <h2 id='version-history'>Version History (Changelog)</h2>
                <div className='d-flex'>
                  <div className='col-md-12 full'>
                    <div className='col-md-12 pl-2'>
                      <p>The version changes are listed below:</p>
                      <h4>Changelog</h4>
                      <div>
                        -----------------------------------------------------------------------------------------
                        Version 3.0 - Feb 27th, 2023
                        -----------------------------------------------------------------------------------------
                        <br />
                        - Changes in Template Layout
                        <br />
                        - Lockplate selection, Drift Id and auto calculation of
                        Motor HP can be selected from Template
                        <br />
                        - First Name of users are displayed in the log-in screen
                        <br />
                        - Auto calculate checkbox for Gas Compressibility and
                        Bubble Point Pressure in the inputs tab
                        <br />
                        - Metric unit correction for Rs, Bg, Bo, GOR, GLR
                        <br />
                        - Filter gas separator dropdown box based on Total Fluid
                        Volume
                        <br />
                        - Filter GKX dropdown based on Total Fluid Volume
                        <br />
                        - Auto efficiency corrected in metric projects
                        <br />
                        - Calculations for separator is hidden when no separator
                        is selected
                        <br />
                        - Unit correction for GOR and GLR in pump page
                        <br />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default CalculationHelper;
